/* ============================================================
   Hero Slider — autoplay, prev/next, dot navigation, fade/slide
   ============================================================ */

class HeroSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.hero-slider__track');
    this.slides = Array.from(this.querySelectorAll('.hero-slider__slide'));
    this.dots = Array.from(this.querySelectorAll('.hero-slider__dot'));
    this.prevBtn = this.querySelector('[data-direction="prev"]');
    this.nextBtn = this.querySelector('[data-direction="next"]');

    this.current = 0;
    this.total = this.slides.length;
    this.mode = this.dataset.transition || 'slide';
    this.autoplay = this.dataset.autoplay === 'true';
    this.defaultDuration = parseInt(this.dataset.autoplayDuration || '5', 10) * 1000;

    // Single slide: still apply height (skip nav/autoplay machinery).
    if (this.total < 2) {
      this.applyHeightStyles();
      return;
    }

    this.bindEvents();
    this.applyHeightStyles();
    this.go(0, true);

    if (this.autoplay) this.startAutoplay();

    this.addEventListener('mouseenter', () => this.pauseAutoplay());
    this.addEventListener('mouseleave', () => { if (this.autoplay) this.startAutoplay(); });
  }

  applyHeightStyles() {
    // Parse height vars and apply to each slide's media area
    this.slides.forEach((slide) => {
      const styles = slide.style;
      const heightDesktop = styles.getPropertyValue('--hs-h-d').trim();
      const heightMobile = styles.getPropertyValue('--hs-h-m').trim();
      const media = slide;

      if (window.matchMedia('(min-width: 750px)').matches) {
        this.applyHeightString(media, heightDesktop);
      } else {
        this.applyHeightString(media, heightMobile);
      }
    });

    window.addEventListener('resize', () => {
      this.slides.forEach((slide) => {
        const heightDesktop = slide.style.getPropertyValue('--hs-h-d').trim();
        const heightMobile = slide.style.getPropertyValue('--hs-h-m').trim();
        if (window.matchMedia('(min-width: 750px)').matches) {
          this.applyHeightString(slide, heightDesktop);
        } else {
          this.applyHeightString(slide, heightMobile);
        }
      });
    });
  }

  applyHeightString(el, str) {
    if (!str) return;
    if (str.startsWith('height:')) {
      const val = str.replace('height:', '').trim();
      el.style.height = val;
      el.style.aspectRatio = '';
    } else if (str.startsWith('aspect-ratio:')) {
      const val = str.replace('aspect-ratio:', '').trim();
      el.style.aspectRatio = val;
      el.style.height = '';
    }
  }

  bindEvents() {
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => { this.go(this.current - 1); this.resetAutoplay(); });
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => { this.go(this.current + 1); this.resetAutoplay(); });

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.dataset.slideTarget, 10);
        this.go(idx);
        this.resetAutoplay();
      });
    });

    // touch swipe
    let startX = 0;
    let endX = 0;
    this.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
    this.addEventListener('touchmove', (e) => { endX = e.touches[0].clientX; }, { passive: true });
    this.addEventListener('touchend', () => {
      const diff = startX - endX;
      if (Math.abs(diff) > 40) {
        if (diff > 0) this.go(this.current + 1);
        else this.go(this.current - 1);
        this.resetAutoplay();
      }
      startX = 0;
      endX = 0;
    });
  }

  go(index, isInit = false) {
    let next = index;
    if (next < 0) next = this.total - 1;
    if (next >= this.total) next = 0;

    this.slides[this.current]?.classList.remove('is-active');
    this.dots[this.current]?.classList.remove('is-active');

    this.current = next;

    this.slides[this.current]?.classList.add('is-active');
    this.dots[this.current]?.classList.add('is-active');

    if (this.mode === 'slide') {
      this.track.style.transform = `translateX(-${this.current * 100}%)`;
    }
  }

  startAutoplay() {
    this.pauseAutoplay();
    const slideDuration = (parseInt(this.slides[this.current]?.dataset.slideDuration || '0', 10) * 1000) || this.defaultDuration;
    this._timer = setTimeout(() => {
      this.go(this.current + 1);
      this.startAutoplay();
    }, slideDuration);
  }

  pauseAutoplay() {
    if (this._timer) {
      clearTimeout(this._timer);
      this._timer = null;
    }
  }

  resetAutoplay() {
    if (this.autoplay) this.startAutoplay();
  }
}

if (!customElements.get('hero-slider')) {
  customElements.define('hero-slider', HeroSlider);
}
